import { zodResolver } from '@hookform/resolvers/zod';
import { Check, ChevronDown, MapPin, Star } from 'lucide-react';
import { useState, type CSSProperties, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { contactMessageInput, type ContactResult, type ResolvedBlock, type SiteBranchInfo, type SiteImage } from '@barbeiropro/contracts';
import { MaskedInput } from '@/components/shared/masked-input';
import { HttpError } from '@/lib/api';
import { money } from '@/lib/format';
import { cn } from '@/lib/utils';
import { pub } from './pub-api';
import { pagePath, SiteLink, useSite } from './site-context';
import { displayStyle, groupHours, readable, toneVars } from './theme';

type Block<T extends ResolvedBlock['type']> = Extract<ResolvedBlock, { type: T }>;

const Img = ({ img, className, eager, style }: { img: SiteImage; className?: string; eager?: boolean; style?: CSSProperties }) => (
  <img
    src={img.url}
    alt={img.alt}
    width={img.width ?? undefined}
    height={img.height ?? undefined}
    loading={eager ? 'eager' : 'lazy'}
    decoding="async"
    className={className}
    style={style}
  />
);

/** Faixa do poste de barbeiro, usada como assinatura visual. */
const poleStyle: CSSProperties = {
  backgroundImage:
    'repeating-linear-gradient(-45deg, var(--s-accent) 0 12px, var(--s-bg-pole, #f4f1ea) 12px 24px, var(--s-primary) 24px 36px, var(--s-bg-pole, #f4f1ea) 36px 48px)',
};
export const Pole = ({ className }: { className?: string }) => <div aria-hidden className={className} style={poleStyle} />;

function Heading({ children, sub, id }: { children: ReactNode; sub?: string; id: string }) {
  return (
    <header className="mb-10 max-w-3xl @4xl:mb-14">
      <h2 id={id} className="text-[clamp(2.25rem,7cqw,4.25rem)]" style={displayStyle}>
        {children}
      </h2>
      {sub && <p className="mt-4 max-w-xl text-base" style={{ color: 'var(--s-muted)' }}>{sub}</p>}
    </header>
  );
}

function Section({ id, tone, children, className }: { id: string; tone: 'light' | 'dark'; children: ReactNode; className?: string }) {
  const { site } = useSite();
  const t = site.theme;
  const style = tone === 'dark' ? toneVars({ bg: t.primary, ink: readable(t.primary) }) : undefined;
  return (
    <section aria-labelledby={`${id}-t`} className={cn('px-5 py-16 @2xl:px-10 @4xl:py-24', className)} style={style}>
      <div className="mx-auto max-w-6xl">{children}</div>
    </section>
  );
}

export function SiteButton({ href, children, variant = 'solid', className }: { href: string; children: ReactNode; variant?: 'solid' | 'outline' | 'ink'; className?: string }) {
  const base = 'inline-flex min-h-12 items-center justify-center px-7 text-[0.95rem] font-semibold tracking-wide transition-transform active:translate-y-px focus-visible:outline-2 focus-visible:outline-offset-2';
  const styles: Record<string, CSSProperties> = {
    solid: { background: 'var(--s-accent)', color: 'var(--s-on-accent)' },
    outline: { border: '2px solid currentColor' },
    ink: { background: 'var(--s-ink)', color: 'var(--s-bg)' },
  };
  return (
    <SiteLink href={href} className={cn(base, className)} style={styles[variant]}>
      {children}
    </SiteLink>
  );
}

// ---------------------------------------------------------------- hero
function Hero({ b }: { b: Block<'hero'> }) {
  const img = b.image;
  return (
    <section aria-labelledby={`${b.id}-t`} className="relative isolate flex min-h-[min(88svh,50rem)] flex-col justify-end overflow-hidden text-[#fbf9f4]" style={{ background: 'var(--s-primary)', color: img ? '#fbf9f4' : 'var(--s-on-primary)' }}>
      {img && (
        <>
          <Img img={img} eager className="absolute inset-0 -z-20 size-full object-cover" />
          <div aria-hidden className="absolute inset-0 -z-10 bg-black/50" />
        </>
      )}
      <Pole className="absolute inset-y-0 left-0 w-3 @2xl:w-4" />
      <div className="mx-auto w-full max-w-6xl px-8 pb-14 pt-32 @2xl:px-14 @4xl:pb-20">
        {b.eyebrow && (
          <p className="mb-5 flex items-center gap-3 text-sm font-semibold tracking-[0.18em]">
            <span aria-hidden className="h-0.5 w-10" style={{ background: 'var(--s-accent)' }} />
            {b.eyebrow}
          </p>
        )}
        <h1 id={`${b.id}-t`} className="max-w-5xl text-balance" style={{ ...displayStyle, fontSize: 'clamp(3.25rem, 15cqw, 9.5rem)' }}>
          {b.title}
        </h1>
        {b.subtitle && <p className="mt-6 max-w-xl text-lg @4xl:text-xl" style={{ opacity: 0.92 }}>{b.subtitle}</p>}
        {(b.primaryCta || b.secondaryCta) && (
          <div className="mt-9 flex flex-wrap gap-3">
            {b.primaryCta && <SiteButton href={b.primaryCta.href}>{b.primaryCta.label}</SiteButton>}
            {b.secondaryCta && <SiteButton href={b.secondaryCta.href} variant="outline">{b.secondaryCta.label}</SiteButton>}
          </div>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------- serviços (tabela de preços)
function Services({ b, tone }: { b: Block<'services'>; tone: 'light' | 'dark' }) {
  const groups = new Map<string, Block<'services'>['items']>();
  for (const s of b.items) groups.set(s.categoryName ?? '', [...(groups.get(s.categoryName ?? '') ?? []), s]);
  return (
    <Section id={b.id} tone={tone}>
      <Heading id={`${b.id}-t`} sub={b.subtitle}>{b.title}</Heading>
      {b.items.length === 0 ? (
        <p style={{ color: 'var(--s-muted)' }}>Em breve, nossos serviços por aqui.</p>
      ) : (
        <div className="grid gap-x-16 gap-y-12 @4xl:grid-cols-2">
          {[...groups.entries()].map(([cat, items]) => (
            <div key={cat}>
              {cat && (
                <h3 className="mb-4 border-b-2 pb-2 text-2xl" style={{ ...displayStyle, borderColor: 'var(--s-ink)' }}>
                  {cat}
                </h3>
              )}
              <ul>
                {items.map((s) => (
                  <li key={s.id} className="border-b py-4" style={{ borderColor: 'var(--s-line)' }}>
                    <div className="flex items-baseline gap-3">
                      <span className="text-lg font-semibold">{s.name}</span>
                      <span aria-hidden className="mb-1 min-w-4 flex-1 self-end border-b border-dotted" style={{ borderColor: 'var(--s-muted)' }} />
                      {s.priceCents !== null && <span className="tabular text-lg font-bold">{money(s.priceCents)}</span>}
                    </div>
                    <p className="mt-1 text-sm" style={{ color: 'var(--s-muted)' }}>
                      {s.durationMinutes} min{s.description ? ` · ${s.description}` : ''}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
      <div className="mt-12">
        <SiteButton href="#agendar" variant={tone === 'dark' ? 'solid' : 'ink'}>Agendar horário</SiteButton>
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------- equipe
function Barbers({ b, tone }: { b: Block<'barbers'>; tone: 'light' | 'dark' }) {
  return (
    <Section id={b.id} tone={tone}>
      <Heading id={`${b.id}-t`} sub={b.subtitle}>{b.title}</Heading>
      {b.items.length === 0 ? (
        <p style={{ color: 'var(--s-muted)' }}>Nossa equipe aparece aqui assim que for cadastrada.</p>
      ) : (
        <ul className="grid gap-x-8 gap-y-14 @2xl:grid-cols-2 @4xl:grid-cols-3">
          {b.items.map((p, i) => (
            <li key={p.id} className={cn(i % 2 === 1 && '@2xl:mt-14', i % 3 === 1 && '@4xl:mt-20', i % 3 === 2 && '@4xl:mt-8')}>
              <div className="aspect-[4/5] w-full overflow-hidden" style={{ background: 'var(--s-line)' }}>
                {p.photoUrl ? (
                  <img src={p.photoUrl} alt={`Foto de ${p.displayName}`} width={640} height={800} loading="lazy" decoding="async" className="size-full object-cover grayscale-[20%]" />
                ) : (
                  <div aria-hidden className="flex size-full items-center justify-center text-8xl" style={{ ...displayStyle, color: 'var(--s-muted)' }}>
                    {p.displayName.slice(0, 1)}
                  </div>
                )}
              </div>
              <h3 className="mt-4 text-3xl" style={displayStyle}>{p.displayName}</h3>
              {p.specialties.length > 0 && <p className="mt-1 text-sm font-semibold" style={{ color: 'var(--s-muted)' }}>{p.specialties.join(' · ')}</p>}
              {p.bio && <p className="mt-2 max-w-sm text-[0.95rem] leading-relaxed">{p.bio}</p>}
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------- galeria
function Gallery({ b, tone }: { b: Block<'gallery'>; tone: 'light' | 'dark' }) {
  return (
    <Section id={b.id} tone={tone}>
      <Heading id={`${b.id}-t`}>{b.title}</Heading>
      {b.images.length === 0 ? (
        <p style={{ color: 'var(--s-muted)' }}>A galeria ainda não tem fotos.</p>
      ) : (
        <div className="columns-2 gap-3 @4xl:columns-3 @4xl:gap-4">
          {b.images.map((img, i) => (
            <Img key={`${img.url}-${i}`} img={img} className="mb-3 w-full break-inside-avoid @4xl:mb-4" />
          ))}
        </div>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------- avaliações
const Stars = ({ n }: { n: number }) => (
  <span role="img" aria-label={`${n} de 5 estrelas`} className="inline-flex gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} aria-hidden className="size-4" style={{ color: 'var(--s-accent)', fill: i <= n ? 'var(--s-accent)' : 'transparent' }} />
    ))}
  </span>
);

function Reviews({ b, tone }: { b: Block<'reviews'>; tone: 'light' | 'dark' }) {
  const [first, ...rest] = b.items;
  return (
    <Section id={b.id} tone={tone}>
      <Heading id={`${b.id}-t`}>{b.title}</Heading>
      {!first ? (
        <p style={{ color: 'var(--s-muted)' }}>As avaliações dos clientes aparecem aqui.</p>
      ) : (
        <div className="grid gap-12 @4xl:grid-cols-[1.4fr_1fr]">
          <figure>
            <Stars n={first.rating} />
            <blockquote className="mt-4 text-[clamp(1.5rem,4.5cqw,2.6rem)] leading-tight" style={{ ...displayStyle, textTransform: 'none', lineHeight: 1.12 }}>
              “{first.comment}”
            </blockquote>
            <figcaption className="mt-5 text-sm font-semibold">{first.customerName}</figcaption>
            {b.average !== null && (
              <p className="mt-8 text-sm" style={{ color: 'var(--s-muted)' }}>
                Nota média <span className="tabular font-bold" style={{ color: 'var(--s-ink)' }}>{b.average.toString().replace('.', ',')}</span> nas avaliações exibidas
              </p>
            )}
          </figure>
          {rest.length > 0 && (
            <ul className="grid content-start gap-7">
              {rest.map((r) => (
                <li key={r.id} className="border-l-4 pl-5" style={{ borderColor: 'var(--s-accent)' }}>
                  <Stars n={r.rating} />
                  <p className="mt-2 text-[0.95rem] leading-relaxed">{r.comment}</p>
                  <p className="mt-2 text-sm font-semibold">{r.customerName}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------- localização
function BranchInfo({ br }: { br: SiteBranchInfo }) {
  const a = br.address;
  return (
    <address className="not-italic">
      <p className="text-xl font-semibold">{br.name}</p>
      <p className="mt-1">{a.street}, {a.number}{a.complement ? ` - ${a.complement}` : ''}</p>
      <p>{a.district} · {a.city}/{a.state}</p>
    </address>
  );
}

function Location({ b, tone }: { b: Block<'location'>; tone: 'light' | 'dark' }) {
  const br = b.branch;
  const q = br ? encodeURIComponent(`${br.address.street}, ${br.address.number}, ${br.address.district}, ${br.address.city} - ${br.address.state}`) : '';
  return (
    <Section id={b.id} tone={tone}>
      <Heading id={`${b.id}-t`}>{b.title}</Heading>
      {!br ? (
        <p style={{ color: 'var(--s-muted)' }}>O endereço será informado em breve.</p>
      ) : (
        <div className="grid gap-10 @4xl:grid-cols-2">
          <div className="grid content-start gap-8">
            <BranchInfo br={br} />
            {b.showHours && br.hours.length > 0 && (
              <dl className="grid max-w-sm gap-2">
                {groupHours(br.hours).map((g) => (
                  <div key={g.days} className="flex justify-between gap-4 border-b py-2" style={{ borderColor: 'var(--s-line)' }}>
                    <dt className="font-semibold">{g.days}</dt>
                    <dd className="tabular">{g.label}</dd>
                  </div>
                ))}
              </dl>
            )}
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${q}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex w-fit items-center gap-2 font-semibold underline underline-offset-4"
            >
              <MapPin className="size-4" aria-hidden /> Abrir no mapa
            </a>
          </div>
          {b.showMap && (
            <iframe
              title={`Mapa: ${br.name}`}
              src={`https://www.google.com/maps?q=${q}&output=embed`}
              loading="lazy"
              referrerPolicy="no-referrer"
              sandbox="allow-scripts allow-same-origin"
              className="h-80 w-full border-0 @4xl:h-full @4xl:min-h-96"
            />
          )}
        </div>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------- cta / texto / faq
function Cta({ b }: { b: Block<'cta'> }) {
  return (
    <section aria-labelledby={`${b.id}-t`} className="px-5 py-20 @2xl:px-10 @4xl:py-28" style={{ background: 'var(--s-accent)', color: 'var(--s-on-accent)' }}>
      <div className="mx-auto flex max-w-6xl flex-col gap-8 @4xl:flex-row @4xl:items-end @4xl:justify-between">
        <div className="max-w-3xl">
          <h2 id={`${b.id}-t`} className="text-[clamp(2.5rem,9cqw,6rem)] text-balance" style={displayStyle}>{b.title}</h2>
          {b.body && <p className="mt-4 max-w-xl text-lg">{b.body}</p>}
        </div>
        <SiteButton href={b.button.href} variant="ink" className="shrink-0">{b.button.label}</SiteButton>
      </div>
    </section>
  );
}

function TextBlock({ b, tone }: { b: Block<'text'>; tone: 'light' | 'dark' }) {
  const paragraphs = b.body.split(/\n{1,}/).filter(Boolean);
  return (
    <Section id={b.id} tone={tone}>
      <div className="max-w-2xl">
        {b.title && <h2 id={`${b.id}-t`} className="mb-6 text-[clamp(2rem,6cqw,3.5rem)]" style={displayStyle}>{b.title}</h2>}
        <div className="grid gap-4 text-[1.05rem] leading-relaxed">
          {paragraphs.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>
      </div>
    </Section>
  );
}

function Faq({ b, tone }: { b: Block<'faq'>; tone: 'light' | 'dark' }) {
  return (
    <Section id={b.id} tone={tone}>
      <Heading id={`${b.id}-t`}>{b.title}</Heading>
      <div className="max-w-3xl border-t" style={{ borderColor: 'var(--s-ink)' }}>
        {b.items.map((it, i) => (
          <details key={i} className="group border-b" style={{ borderColor: 'var(--s-line)' }}>
            <summary className="flex cursor-pointer list-none items-center justify-between gap-4 py-5 text-lg font-semibold [&::-webkit-details-marker]:hidden">
              {it.q}
              <ChevronDown className="size-5 shrink-0 transition-transform group-open:rotate-180" aria-hidden />
            </summary>
            <p className="pb-5 pr-10 leading-relaxed" style={{ color: 'var(--s-muted)' }}>{it.a}</p>
          </details>
        ))}
        {b.items.length === 0 && <p className="py-5" style={{ color: 'var(--s-muted)' }}>Nenhuma pergunta cadastrada ainda.</p>}
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------- contato
function ContactForm({ b, tone }: { b: Block<'contact_form'>; tone: 'light' | 'dark' }) {
  const { slug, preview, pageSlug } = useSite();
  const navigate = useNavigate();
  const [done, setDone] = useState<string | null>(null);
  const form = useForm({ resolver: zodResolver(contactMessageInput), defaultValues: { name: '', email: '', phone: '', message: '', pageSlug, website: '' } });
  const { errors, isSubmitting } = form.formState;
  const field = 'w-full border-2 bg-transparent px-3.5 py-3 text-base focus-visible:outline-2 focus-visible:outline-offset-2';
  const fs = { borderColor: 'var(--s-line)' } as CSSProperties;

  async function submit(values: Record<string, unknown>) {
    if (preview) return toast.info('Na pré-visualização o formulário não envia mensagens.');
    try {
      const r = await pub<ContactResult>('POST', `/public/${slug}/contact`, values);
      if (r.successPageSlug) return navigate(pagePath(slug, r.successPageSlug));
      setDone(r.successMessage);
    } catch (e) {
      toast.error(e instanceof HttpError && e.status === 429 ? 'Muitas tentativas. Aguarde um minuto.' : 'Não foi possível enviar. Tente de novo.');
    }
  }

  return (
    <Section id={b.id} tone={tone}>
      <div className="grid gap-10 @4xl:grid-cols-[1fr_1.2fr]">
        <Heading id={`${b.id}-t`} sub={b.subtitle}>{b.title}</Heading>
        {done ? (
          <div role="status" className="flex items-start gap-3 self-start border-2 p-6" style={{ borderColor: 'var(--s-accent)' }}>
            <Check className="mt-0.5 size-5 shrink-0" aria-hidden /> <p className="text-lg">{done}</p>
          </div>
        ) : (
          <form onSubmit={form.handleSubmit(submit)} noValidate className="grid gap-4">
            <div aria-hidden className="absolute -left-[9999px] h-0 w-0 overflow-hidden">
              <label>Não preencha<input tabIndex={-1} autoComplete="off" {...form.register('website')} /></label>
            </div>
            {(
              [
                ['name', 'Seu nome', 'text', 'name'],
                ['email', 'E-mail', 'email', 'email'],
              ] as const
            ).map(([k, label, type, ac]) => (
              <div key={k} className="grid gap-1.5">
                <label htmlFor={`${b.id}-${k}`} className="text-sm font-semibold">{label}</label>
                <input id={`${b.id}-${k}`} type={type} autoComplete={ac} className={field} style={fs} aria-invalid={!!errors[k]} {...form.register(k)} />
                {errors[k] && <p role="alert" className="text-sm font-medium">{k === 'email' ? 'Informe um e-mail válido.' : 'Informe seu nome.'}</p>}
              </div>
            ))}
            <div className="grid gap-1.5">
              <label htmlFor={`${b.id}-phone`} className="text-sm font-semibold">Telefone (opcional)</label>
              <MaskedInput id={`${b.id}-phone`} mask="phone" autoComplete="tel" className={cn(field, 'h-auto rounded-none border-2 shadow-none')} style={fs} {...form.register('phone')} />
            </div>
            <div className="grid gap-1.5">
              <label htmlFor={`${b.id}-message`} className="text-sm font-semibold">Mensagem</label>
              <textarea id={`${b.id}-message`} rows={5} className={field} style={fs} aria-invalid={!!errors.message} {...form.register('message')} />
              {errors.message && <p role="alert" className="text-sm font-medium">Escreva ao menos 5 caracteres.</p>}
            </div>
            <div>
              <button type="submit" disabled={isSubmitting} className="inline-flex min-h-12 items-center px-7 font-semibold disabled:opacity-60" style={{ background: 'var(--s-ink)', color: 'var(--s-bg)' }}>
                {isSubmitting ? 'Enviando…' : 'Enviar mensagem'}
              </button>
            </div>
          </form>
        )}
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------- renderer
export function BlockRenderer({ blocks }: { blocks: ResolvedBlock[] }) {
  let n = 0;
  return (
    <>
      {blocks.map((b) => {
        if (b.type === 'hero') return <Hero key={b.id} b={b} />;
        if (b.type === 'cta') return <Cta key={b.id} b={b} />;
        const tone: 'light' | 'dark' = n++ % 2 === 0 ? 'light' : 'dark';
        switch (b.type) {
          case 'services':
            return <Services key={b.id} b={b} tone={tone} />;
          case 'barbers':
            return <Barbers key={b.id} b={b} tone={tone} />;
          case 'gallery':
            return <Gallery key={b.id} b={b} tone={tone} />;
          case 'reviews':
            return <Reviews key={b.id} b={b} tone={tone} />;
          case 'location':
            return <Location key={b.id} b={b} tone={tone} />;
          case 'text':
            return <TextBlock key={b.id} b={b} tone={tone} />;
          case 'faq':
            return <Faq key={b.id} b={b} tone={tone} />;
          case 'contact_form':
            return <ContactForm key={b.id} b={b} tone={tone} />;
        }
      })}
    </>
  );
}
